/**
 * RecipientItem Component (Refactored)
 *
 * Displays a single email recipient with verification status, alert level
 * preferences, channel toggles, and action buttons.
 *
 * Architecture:
 * - Uses extracted sub-components for UI elements
 * - Uses custom hooks for state management
 * - Zero inline styles (CSS class-based)
 * - WCAG 2.1 AA compliant
 *
 * @module RecipientItem
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { BlockStack, InlineStack, Box } from '@shopify/polaris';

import type { RecipientItemProps } from './types';
import type { ModalMessage } from '~/hooks/core/useModalCommunication';
import { useVerificationFlow } from './hooks/useVerificationFlow';
import { useAnimationStates } from './hooks/useAnimationStates';
import { useModalRoute } from '~/hooks/core/useModalRoute';
import { AppBridgeModal } from '../../app-bridge/AppBridgeModal';
import { RecipientAvatar } from './RecipientAvatar';
import { RecipientInfo } from './RecipientInfo';
import { RecipientActions, MobileRecipientActions } from './RecipientActions';
import { VerificationInput } from './VerificationInput';

export function RecipientItem({
  recipient,
  onSendVerification,
  onVerifyCode,
  onTest,
  onEdit,
  onDelete,
  onToggleChannel,
  isDeleting = false,
  isNew = false,
  isAnimatingOut = false,
  onDeleteAnimationComplete,
  isLoadingIn = false,
  updatedFields,
}: RecipientItemProps) {
  const [searchParams] = useSearchParams();
  
  // Route-based modal for delete confirmation
  const deleteModal = useModalRoute('/app/settings/modal/delete-recipient');

  // UI state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [alertBadgesExpanded, setAlertBadgesExpanded] = useState(false);

  // Channel toggle loading states
  const [togglingDigest, setTogglingDigest] = useState(false);
  const [togglingInstant, setTogglingInstant] = useState(false);

  // Refs
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Custom hooks for complex state management
  const verification = useVerificationFlow({
    recipient,
    onSendVerification,
    onVerifyCode,
    onTest,
  });

  const animations = useAnimationStates({
    isNew,
    isLoadingIn,
    isAnimatingOut,
    recipient,
    onDeleteAnimationComplete,
  });

  // Sync verification state when recipient verification status changes externally
  useEffect(() => {
    if (recipient.is_verified && verification.state !== 'verified' && verification.state !== 'testing') {
      verification.reset();
    }
  }, [recipient.is_verified, verification.state, verification]);

  // Action handlers
  const handleDeleteClick = useCallback(() => {
    setShowActionsMenu(false);
    const shop = searchParams.get('shop') || '';
    deleteModal.open({
      shop,
      recipientId: String(recipient.id),
      recipientName: recipient.recipient_name,
      recipientEmail: recipient.email_address,
    });
    setShowDeleteConfirmation(true);
  }, [searchParams, recipient, deleteModal]);

  // deletedByModal param indicates the modal already performed the API deletion
  const handleDeleteConfirm = useCallback((deletedByModal = false) => {
    onDelete(recipient, deletedByModal);
    deleteModal.close();
    setShowDeleteConfirmation(false);
  }, [recipient, onDelete, deleteModal]);

  const handleDeleteCancel = useCallback(() => {
    deleteModal.close();
    setShowDeleteConfirmation(false);
  }, [deleteModal]);

  // Handle messages from delete modal
  const handleDeleteModalMessage = useCallback((message: ModalMessage) => {
    if (message.type === 'recipient-deleted') {
      // Deletion was performed by the modal - pass true to skip API call
      handleDeleteConfirm(true);
    }
  }, [handleDeleteConfirm]);

  const handleEditClick = useCallback(() => {
    setShowActionsMenu(false);
    onEdit(recipient);
  }, [recipient, onEdit]);

  const toggleActionsMenu = useCallback(() => {
    setShowActionsMenu((prev) => !prev);
  }, []);

  const toggleAlertBadges = useCallback(() => {
    setAlertBadgesExpanded((prev) => !prev);
  }, []);

  // Channel toggle handlers
  const handleToggleDigest = useCallback(async () => {
    if (!onToggleChannel || !recipient.is_verified || togglingDigest) return;

    setTogglingDigest(true);
    try {
      const newEnabled = !recipient.channels.digest.enabled;
      await onToggleChannel(recipient, 'digest', newEnabled);
    } finally {
      setTogglingDigest(false);
    }
  }, [recipient, onToggleChannel, togglingDigest]);

  const handleToggleInstant = useCallback(async () => {
    if (!onToggleChannel || !recipient.is_verified || togglingInstant) return;

    setTogglingInstant(true);
    try {
      const newEnabled = !recipient.channels.instant.enabled;
      await onToggleChannel(recipient, 'instant', newEnabled);
    } finally {
      setTogglingInstant(false);
    }
  }, [recipient, onToggleChannel, togglingInstant]);

  // Verification action wrappers that close menu first
  const handleVerifyClick = useCallback(() => {
    setShowActionsMenu(false);
    verification.sendVerification();
  }, [verification]);

  const handleTestClick = useCallback(() => {
    setShowActionsMenu(false);
    verification.sendTestEmail();
  }, [verification]);

  // Check if in code input state
  const isInCodeInputState =
    verification.state === 'awaiting_code' ||
    verification.state === 'error' ||
    verification.state === 'verifying';

  // Check if recipient was just updated (any fields changed)
  const wasJustUpdated = updatedFields && updatedFields.size > 0;

  // Build CSS classes
  const cardClasses = [
    'recipient-card',
    animations.isPopAnimating ? 'recipient-card-new' : '',
    verification.justVerified ? 'recipient-card-just-verified' : '',
    wasJustUpdated ? 'recipient-card-updated' : '',
    recipient.is_verified ? 'recipient-card-verified' : 'recipient-card-unverified',
  ]
    .filter(Boolean)
    .join(' ');

  const wrapperClasses = [
    'recipient-item-wrapper',
    isAnimatingOut ? 'deleting' : '',
    animations.isDeleteAnimating ? 'collapsed' : '',
    animations.isLoadAnimating ? 'load-in' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={wrapperRef}
      className={wrapperClasses}
      data-collapsed={animations.isDeleteAnimating}
    >
      <div
        className={cardClasses}
        id={`recipient-${recipient.id}`}
        data-verified={recipient.is_verified}
      >
        <BlockStack gap="300">
          <InlineStack
            gap="400"
            blockAlign="center"
            wrap={false}
            key={`recipient-${recipient.id}-content`}
          >
            {/* Avatar with verification status */}
            <RecipientAvatar
              name={recipient.recipient_name}
              isVerified={recipient.is_verified}
            />

            {/* Name and email */}
            <RecipientInfo
              name={recipient.recipient_name}
              email={recipient.email_address}
              isVerified={recipient.is_verified}
            />

            {/* Desktop Actions */}
            <div
              className={`channel-actions-desktop ${verification.justVerified ? 'recipient-verified-pop' : ''}`}
            >
              <InlineStack gap="100" blockAlign="center">
                {/* Inline verification input */}
                <VerificationInput
                  code={verification.code}
                  verificationState={verification.state}
                  isVisible={verification.isCodeInputVisible}
                  isExiting={verification.isCodeInputExiting}
                  onCodeChange={verification.setCode}
                  onSubmit={verification.submitCode}
                  onResend={verification.resendCode}
                />

                {/* Verification/Test actions with channel badges */}
                {!isInCodeInputState && (
                  <RecipientActions
                    recipientName={recipient.recipient_name}
                    isVerified={recipient.is_verified}
                    verificationState={verification.state}
                    showActionsMenu={showActionsMenu}
                    onToggleActionsMenu={toggleActionsMenu}
                    onVerify={handleVerifyClick}
                    onTest={handleTestClick}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                    channels={recipient.channels}
                    alertLevels={recipient.alert_levels}
                    isTogglingDigest={togglingDigest}
                    isTogglingInstant={togglingInstant}
                    onToggleDigest={handleToggleDigest}
                    onToggleInstant={handleToggleInstant}
                    alertBadgesExpanded={alertBadgesExpanded}
                    onToggleAlertBadges={toggleAlertBadges}
                    showBadgesPopAnimation={verification.justVerified}
                  />
                )}

                {/* Actions menu when in code input state */}
                {isInCodeInputState && (
                  <Box opacity={recipient.is_verified ? undefined : '50'}>
                    <RecipientActions
                      recipientName={recipient.recipient_name}
                      isVerified={recipient.is_verified}
                      verificationState={verification.state}
                      showActionsMenu={showActionsMenu}
                      onToggleActionsMenu={toggleActionsMenu}
                      onVerify={handleVerifyClick}
                      onTest={handleTestClick}
                      onEdit={handleEditClick}
                      onDelete={handleDeleteClick}
                      hideVerificationButton
                      channels={recipient.channels}
                      alertLevels={recipient.alert_levels}
                      isTogglingDigest={togglingDigest}
                      isTogglingInstant={togglingInstant}
                      onToggleDigest={handleToggleDigest}
                      onToggleInstant={handleToggleInstant}
                      alertBadgesExpanded={alertBadgesExpanded}
                      onToggleAlertBadges={toggleAlertBadges}
                      showBadgesPopAnimation={verification.justVerified}
                    />
                  </Box>
                )}
              </InlineStack>
            </div>

            {/* Mobile Actions */}
            <div className="channel-actions-mobile">
              <MobileRecipientActions
                isVerified={recipient.is_verified}
                verificationState={verification.state}
                showActionsMenu={showActionsMenu}
                onToggleActionsMenu={toggleActionsMenu}
                onVerify={handleVerifyClick}
                onTest={handleTestClick}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />
            </div>
          </InlineStack>
        </BlockStack>
      </div>

      {/* Delete Confirmation Modal - Route-based */}
      <AppBridgeModal
        src={deleteModal.src}
        open={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        variant="base"
        onMessage={handleDeleteModalMessage}
        title="Delete email recipient"
      />
    </div>
  );
}
